import React, { useState, useEffect } from 'react';
import {
    Box,
    Container,
    Avatar,
    Typography,
    Tabs,
    Tab,
    CircularProgress,
    Button,
    TextField,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
} from '@mui/material';
import {
    Edit as EditIcon,
} from '@mui/icons-material';
import { useTheme } from '../contexts/ThemeContext';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { getUserProfile, updateUserBio, PublicUserProfile } from '../services/userService';
import { getUserPosts } from '../services/socialService';
import { getFollowCounts } from '../services/followService';
import { FollowButton } from '../components/social/FollowButton';
import { PostCard } from '../components/social/PostCard';
import { useSocial } from '../hooks/useSocial';
import { Post } from '../types/social';
import { TopBar } from '../layout/TopBar';
import { BottomNavigation } from '../layout/BottomNavigation';

export const UserProfilePage: React.FC = () => {
    const { theme } = useTheme();
    const navigate = useNavigate();
    const { userId } = useParams<{ userId: string }>();
    const { user } = useAuth();
    const [profile, setProfile] = useState<PublicUserProfile | null>(null);
    const [posts, setPosts] = useState<Post[]>([]);
    const [loading, setLoading] = useState(true);
    const [currentTab, setCurrentTab] = useState(0);
    const [followCounts, setFollowCounts] = useState({ followers: 0, following: 0 });
    const [editBioOpen, setEditBioOpen] = useState(false);
    const [newBio, setNewBio] = useState('');

    const { toggleLike, checkLike, toggleFavorite, checkFavorite, addPostComment, getComments, removePost } = useSocial();

    const isOwnProfile = user?.uid === userId;

    useEffect(() => {
        loadProfile();
    }, [userId]);

    const loadProfile = async () => {
        if (!userId) return;

        try {
            setLoading(true);

            // Carregar perfil
            const userProfile = await getUserProfile(userId);
            if (!userProfile) {
                alert('Usuário não encontrado');
                navigate('/hostlyclub');
                return;
            }
            setProfile(userProfile);
            setNewBio(userProfile.bio || '');

            // Carregar posts do usuário
            const userPosts = await getUserPosts(userId);
            setPosts(userPosts);

            // Carregar contadores de follow
            const counts = await getFollowCounts(userId);
            setFollowCounts(counts);
        } catch (error) {
            console.error('Erro ao carregar perfil:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleUpdateBio = async () => {
        if (!userId) return;

        try {
            await updateUserBio(userId, newBio);
            setProfile(prev => prev ? { ...prev, bio: newBio } : null);
            setEditBioOpen(false);
        } catch (error) {
            console.error('Erro ao atualizar bio:', error);
            alert('Erro ao atualizar bio');
        }
    };

    const handleFollowChange = () => {
        // Recarregar contadores após seguir/deixar de seguir
        if (userId) {
            getFollowCounts(userId).then(setFollowCounts);
        }
    };

    if (loading) {
        return (
            <>
                <TopBar
                    title="Perfil"
                    showBackButton
                    onBackClick={() => navigate(-1)}
                />
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
                    <CircularProgress sx={{ color: theme.colors.primary }} />
                </Box>
            </>
        );
    }

    if (!profile) {
        return (
            <>
                <TopBar
                    title="Perfil"
                    showBackButton
                    onBackClick={() => navigate(-1)}
                />
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
                    <Typography sx={{ color: theme.colors.text.primary }}>Usuário não encontrado</Typography>
                </Box>
            </>
        );
    }

    return (
        <>
            <TopBar
                title={profile.name}
                showBackButton
                onBackClick={() => navigate(-1)}
            />

            <Box
                sx={{
                    backgroundColor: theme.colors.background,
                    minHeight: '100vh',
                    paddingBottom: '120px',
                    paddingTop: '70px',
                }}
            >
                <Container maxWidth="sm" sx={{ padding: 0 }}>
                    {/* Header do Perfil - Estilo Instagram */}
                    <Box sx={{ p: 3, backgroundColor: theme.colors.card }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                            <Avatar
                                src={profile.profilePictureUrl}
                                sx={{
                                    width: 80,
                                    height: 80,
                                    backgroundColor: theme.colors.primary,
                                    mr: 3,
                                }}
                            >
                                {profile.name?.charAt(0)?.toUpperCase() || 'U'}
                            </Avatar>

                            <Box sx={{ flex: 1 }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-around', mb: 1 }}>
                                    <Box sx={{ textAlign: 'center' }}>
                                        <Typography variant="h6" sx={{ fontWeight: 'bold', color: theme.colors.text.primary }}>
                                            {posts.length}
                                        </Typography>
                                        <Typography variant="caption" sx={{ color: theme.colors.text.secondary }}>
                                            posts
                                        </Typography>
                                    </Box>
                                    <Box sx={{ textAlign: 'center' }}>
                                        <Typography variant="h6" sx={{ fontWeight: 'bold', color: theme.colors.text.primary }}>
                                            {followCounts.followers}
                                        </Typography>
                                        <Typography variant="caption" sx={{ color: theme.colors.text.secondary }}>
                                            seguidores
                                        </Typography>
                                    </Box>
                                    <Box sx={{ textAlign: 'center' }}>
                                        <Typography variant="h6" sx={{ fontWeight: 'bold', color: theme.colors.text.primary }}>
                                            {followCounts.following}
                                        </Typography>
                                        <Typography variant="caption" sx={{ color: theme.colors.text.secondary }}>
                                            seguindo
                                        </Typography>
                                    </Box>
                                </Box>
                            </Box>
                        </Box>

                        <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: theme.colors.text.primary, mb: 0.5 }}>
                            {profile.name}
                        </Typography>

                        {profile.bio && (
                            <Typography variant="body2" sx={{ color: theme.colors.text.primary, mb: 2 }}>
                                {profile.bio}
                            </Typography>
                        )}

                        {/* Botões de Ação */}
                        <Box sx={{ display: 'flex', gap: 1 }}>
                            {isOwnProfile ? (
                                <Button
                                    fullWidth
                                    variant="outlined"
                                    startIcon={<EditIcon />}
                                    onClick={() => setEditBioOpen(true)}
                                    sx={{
                                        borderColor: theme.colors.border,
                                        color: theme.colors.text.primary,
                                        textTransform: 'none',
                                        fontWeight: 'bold',
                                    }}
                                >
                                    Editar perfil
                                </Button>
                            ) : (
                                <FollowButton
                                    targetUserId={userId!}
                                    variant="contained"
                                    size="medium"
                                    onFollowChange={handleFollowChange}
                                />
                            )}
                        </Box>
                    </Box>

                    {/* Tabs */}
                    <Tabs
                        value={currentTab}
                        onChange={(_, newValue) => setCurrentTab(newValue)}
                        variant="fullWidth"
                        sx={{
                            backgroundColor: theme.colors.card,
                            borderBottom: `1px solid ${theme.colors.border}`,
                            '& .MuiTab-root': {
                                color: theme.colors.text.secondary,
                                '&.Mui-selected': {
                                    color: theme.colors.primary,
                                },
                            },
                            '& .MuiTabs-indicator': {
                                backgroundColor: theme.colors.primary,
                            },
                        }}
                    >
                        <Tab label="Posts" />
                        <Tab label="Salvos" disabled={!isOwnProfile} />
                    </Tabs>

                    {/* Posts Grid */}
                    {currentTab === 0 && (
                        <Box>
                            {posts.length === 0 ? (
                                <Box sx={{ textAlign: 'center', py: 8 }}>
                                    <Typography sx={{ color: theme.colors.text.secondary }}>
                                        Nenhum post ainda
                                    </Typography>
                                </Box>
                            ) : (
                                posts.map((post) => (
                                    <PostCard
                                        key={post.id}
                                        post={post}
                                        onLike={toggleLike}
                                        onFavorite={toggleFavorite}
                                        onComment={addPostComment}
                                        onDelete={isOwnProfile ? removePost : undefined}
                                        checkLike={checkLike}
                                        checkFavorite={checkFavorite}
                                        getComments={getComments}
                                    />
                                ))
                            )}
                        </Box>
                    )}
                </Container>

                {/* Dialog de Editar Bio */}
                <Dialog open={editBioOpen} onClose={() => setEditBioOpen(false)} fullWidth maxWidth="sm">
                    <DialogTitle sx={{ color: theme.colors.text.primary }}>Editar Bio</DialogTitle>
                    <DialogContent>
                        <TextField
                            fullWidth
                            multiline
                            rows={4}
                            value={newBio}
                            onChange={(e) => setNewBio(e.target.value)}
                            placeholder="Conte um pouco sobre você..."
                            sx={{
                                mt: 1,
                                '& .MuiOutlinedInput-root': {
                                    color: theme.colors.text.primary,
                                },
                            }}
                        />
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setEditBioOpen(false)} sx={{ color: theme.colors.text.secondary }}>
                            Cancelar
                        </Button>
                        <Button onClick={handleUpdateBio} variant="contained" sx={{ backgroundColor: theme.colors.primary }}>
                            Salvar
                        </Button>
                    </DialogActions>
                </Dialog>

                <BottomNavigation />
            </Box>
        </>
    );
};
